import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Plus, Minus, Trash2, ShoppingBag, ArrowLeft, ShieldCheck, Truck, X } from 'lucide-react';
import { useCart } from '../context/CartContext';
import { useStore } from '../context/StoreContext';
import { StockBadge } from '../components/StockBadge';
import { YarboCareUpsellPanel } from '../components/YarboCareUpsellPanel';
import { getPlanById } from '../data/yarboCare';
export function Cart() {
  const {
    items,
    removeFromCart,
    updateQuantity,
    removeProtection,
    productSubtotal,
    protectionSubtotal,
    subtotal,
    discountAmount,
    discountCode,
    applyDiscount,
    removeDiscount,
    shippingEstimate,
    taxEstimate,
    total,
    itemCount
  } = useCart();
  const {
    validateDiscount
  } = useStore();
  const [discountInput, setDiscountInput] = useState('');
  const [discountError, setDiscountError] = useState('');
  const handleApplyDiscount = () => {
    const res = validateDiscount(discountInput, subtotal);
    if (res.valid) {
      applyDiscount(discountInput, res.amount);
      setDiscountError('');
      setDiscountInput('');
    } else {
      setDiscountError(res.error || 'Invalid code');
    }
  };
  if (items.length === 0) {
    return <div className="bg-gray-50 min-h-[60vh] flex items-center" data-id="element-992">
        <div className="max-w-md mx-auto text-center px-4 py-16" data-id="element-993">
          <ShoppingBag className="w-16 h-16 text-gray-300 mx-auto mb-6" data-id="element-994" />
          <h1 className="text-3xl font-extrabold text-yarbo-dark mb-3" data-id="element-995">
            Your cart is empty
          </h1>
          <p className="text-gray-600 mb-8" data-id="element-996">
            Browse Yarbo systems, bundles, modules, and accessories.
          </p>
          <Link to="/shop" className="inline-block bg-yarbo-dark text-white font-bold px-8 py-4 rounded-lg hover:bg-black transition-colors" data-id="element-997">
            Shop All Products
          </Link>
        </div>
      </div>;
  }
  return <div className="bg-gray-50 min-h-screen" data-id="element-998">
      <div className="bg-white border-b border-gray-200" data-id="element-999">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-id="element-1000">
          <h1 className="text-3xl md:text-4xl font-extrabold text-yarbo-dark mb-1" data-id="element-1001">
            Your Cart
          </h1>
          <p className="text-gray-600 text-sm" data-id="element-1002">
            {itemCount} item{itemCount !== 1 ? 's' : ''}
          </p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-id="element-1003">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8" data-id="element-1004">
          {/* Items */}
          <div className="lg:col-span-2 space-y-3" data-id="element-1005">
            {items.map(({
            product,
            quantity,
            protectionPlanId
          }) => {
            const plan = getPlanById(protectionPlanId);
            return <motion.div key={product.id} layout className="bg-white rounded-xl border border-gray-200 overflow-hidden" data-id="element-1006">
                  <div className="p-4 sm:p-5 flex gap-4" data-id="element-1007">
                    <Link to={`/product/${product.slug}`} className="w-24 h-24 sm:w-28 sm:h-28 bg-gray-50 rounded-lg p-3 flex-shrink-0" data-id="element-1008">
                      <img src={product.image} alt={product.name} className="w-full h-full object-contain" data-id="element-1009" />
                    </Link>
                    <div className="flex-1 min-w-0 flex flex-col" data-id="element-1010">
                      <div className="flex justify-between gap-3 mb-2" data-id="element-1011">
                        <div data-id="element-1012">
                          <Link to={`/product/${product.slug}`} className="font-bold text-yarbo-dark hover:text-yarbo-yellow leading-tight" data-id="element-1013">
                            {product.name}
                          </Link>
                          <div className="mt-2" data-id="element-1014">
                            <StockBadge status={product.status} data-id="element-1015" />
                          </div>
                        </div>
                        <button onClick={() => removeFromCart(product.id)} className="text-gray-400 hover:text-red-500" aria-label="Remove" data-id="element-1016">
                          <Trash2 className="w-4 h-4" data-id="element-1017" />
                        </button>
                      </div>
                      <div className="flex items-end justify-between mt-auto gap-3" data-id="element-1018">
                        <div className="flex items-center border border-gray-200 rounded-lg" data-id="element-1019">
                          <button onClick={() => updateQuantity(product.id, quantity - 1)} className="p-2 hover:bg-gray-50" data-id="element-1020">
                            <Minus className="w-3.5 h-3.5" data-id="element-1021" />
                          </button>
                          <span className="px-4 font-bold text-sm" data-id="element-1022">
                            {quantity}
                          </span>
                          <button onClick={() => updateQuantity(product.id, quantity + 1)} className="p-2 hover:bg-gray-50" data-id="element-1023">
                            <Plus className="w-3.5 h-3.5" data-id="element-1024" />
                          </button>
                        </div>
                        <div className="text-right" data-id="element-1025">
                          <div className="text-xs text-gray-500" data-id="element-1026">
                            ${product.price.toLocaleString()} each
                          </div>
                          <div className="text-lg font-extrabold text-yarbo-dark" data-id="element-1027">
                            ${(product.price * quantity).toLocaleString()}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {plan && <div className="border-t border-yarbo-yellow/30 bg-yarbo-yellow/10 px-4 sm:px-5 py-3 flex items-center justify-between gap-3" data-id="element-1028">
                      <div className="flex items-center gap-2 text-xs text-yarbo-dark" data-id="element-1029">
                        <ShieldCheck className="w-4 h-4 flex-shrink-0" data-id="element-1030" />
                        <div data-id="element-1031">
                          <span className="font-extrabold" data-id="element-1032">
                            YarboCare Protection
                          </span>
                          <span className="text-gray-700" data-id="element-1033"> · {plan.title}</span>
                        </div>
                      </div>
                      <div className="flex items-center gap-3 flex-shrink-0" data-id="element-1034">
                        <span className="text-sm font-extrabold text-yarbo-dark" data-id="element-1035">
                          +${plan.priceMockup}
                        </span>
                        <button onClick={() => removeProtection(product.id)} className="text-[11px] font-bold text-gray-600 hover:text-yarbo-dark underline inline-flex items-center gap-1" data-id="element-1036">
                          <X className="w-3 h-3" data-id="element-1037" /> Remove
                        </button>
                      </div>
                    </div>}
                </motion.div>;
          })}

            {/* YarboCare upsell panel */}
            <YarboCareUpsellPanel data-id="element-1038" />

            <Link to="/shop" className="inline-flex items-center text-sm font-bold text-gray-700 hover:text-yarbo-dark pt-4" data-id="element-1039">
              <ArrowLeft className="w-4 h-4 mr-2" data-id="element-1040" /> Continue Shopping
            </Link>
          </div>

          {/* Summary */}
          <aside className="lg:col-span-1" data-id="element-1041">
            <div className="bg-white rounded-xl border border-gray-200 p-6 sticky top-24" data-id="element-1042">
              <h2 className="text-lg font-bold text-yarbo-dark mb-4" data-id="element-1043">
                Order Summary
              </h2>

              <div className="space-y-3 pb-4 border-b border-gray-100 mb-4 text-sm" data-id="element-1044">
                <div className="flex justify-between" data-id="element-1045">
                  <span className="text-gray-600" data-id="element-1046">
                    Subtotal ({itemCount} items)
                  </span>
                  <span className="font-bold text-yarbo-dark" data-id="element-1047">
                    ${productSubtotal.toLocaleString()}
                  </span>
                </div>
                {protectionSubtotal > 0 && <div className="flex justify-between" data-id="element-1048">
                    <span className="text-gray-600 flex items-center gap-1" data-id="element-1049">
                      <ShieldCheck className="w-3.5 h-3.5" data-id="element-1050" /> YarboCare
                      Protection
                    </span>
                    <span className="font-bold text-yarbo-dark" data-id="element-1051">
                      +${protectionSubtotal.toLocaleString()}
                    </span>
                  </div>}

                {/* Discount Section */}
                <div className="pt-3 mt-3 border-t border-gray-100" data-id="element-1052">
                  {discountCode ? <div className="bg-green-50 border border-green-200 p-3 rounded-lg flex items-center justify-between" data-id="element-1053">
                      <div data-id="element-1054">
                        <p className="text-green-800 font-bold text-xs" data-id="element-1055">
                          Code Applied: {discountCode}
                        </p>
                        <p className="text-green-600 text-xs" data-id="element-1056">
                          You saved ${discountAmount.toFixed(2)}
                        </p>
                      </div>
                      <button onClick={removeDiscount} className="text-green-800 hover:text-green-900" data-id="element-1057">
                        <X className="w-4 h-4" data-id="element-1058" />
                      </button>
                    </div> : <div className="space-y-2" data-id="element-1059">
                      <div className="flex gap-2" data-id="element-1060">
                        <input type="text" value={discountInput} onChange={e => setDiscountInput(e.target.value)} placeholder="Discount code" className="flex-1 border-gray-300 rounded-lg shadow-sm focus:border-yarbo-dark focus:ring-yarbo-dark text-sm" data-id="element-1061" />
                        <button onClick={handleApplyDiscount} className="bg-gray-900 text-white px-3 py-2 rounded-lg font-bold hover:bg-black text-sm" data-id="element-1062">
                          Apply
                        </button>
                      </div>
                      {discountError && <p className="text-red-600 text-xs" data-id="element-1063">{discountError}</p>}
                    </div>}
                </div>

                {discountAmount > 0 && <div className="flex justify-between text-green-600 pt-3" data-id="element-1064">
                    <span data-id="element-1065">Discount</span>
                    <span className="font-bold" data-id="element-1066">
                      -${discountAmount.toLocaleString()}
                    </span>
                  </div>}
                <div className="flex justify-between pt-3" data-id="element-1067">
                  <span className="text-gray-600" data-id="element-1068">Shipping</span>
                  <span className="font-medium text-gray-500" data-id="element-1069">
                    {shippingEstimate === null ? 'Calculated at checkout' : shippingEstimate === 0 ? 'Free' : `$${shippingEstimate.toLocaleString()}`}
                  </span>
                </div>
                <div className="flex justify-between" data-id="element-1070">
                  <span className="text-gray-600" data-id="element-1071">Estimated Tax</span>
                  <span className="font-medium text-gray-500" data-id="element-1072">
                    {taxEstimate === null ? 'Calculated at checkout' : `$${taxEstimate.toLocaleString(undefined, {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2
                  })}`}
                  </span>
                </div>
              </div>

              <div className="flex justify-between mb-6" data-id="element-1073">
                <span className="text-lg font-bold text-yarbo-dark" data-id="element-1074">
                  Estimated Total
                </span>
                <span className="text-2xl font-extrabold text-yarbo-dark" data-id="element-1075">
                  ${total.toLocaleString()}
                </span>
              </div>

              <Link to="/checkout" className="block w-full text-center bg-yarbo-dark text-white font-bold py-4 rounded-lg hover:bg-black transition-colors text-base" data-id="element-1076">
                Proceed to Checkout
              </Link>

              <div className="mt-5 space-y-2 text-xs text-gray-500" data-id="element-1077">
                <div className="flex items-center gap-2" data-id="element-1078">
                  <ShieldCheck className="w-4 h-4 text-gray-400" data-id="element-1079" /> Secure
                  encrypted checkout
                </div>
                <div className="flex items-center gap-2" data-id="element-1080">
                  <Truck className="w-4 h-4 text-gray-400" data-id="element-1081" /> Nationwide
                  shipping
                </div>
              </div>

              <p className="text-xs text-gray-500 mt-5 pt-4 border-t border-gray-100" data-id="element-1082">
                All items in stock and ready to ship. Shipping and taxes are
                calculated at checkout.
              </p>
            </div>
          </aside>
        </div>
      </div>
    </div>;
}